export type PurchaseOrderItemStatusType = 'READY_TO_ORDER' | 'PRICE_REQUESTED' | 'AWAITING_RELATED_ITEMS' | 'ALL';
export type PurchaseOrderStatusType =
  | 'PROCESSING'
  | 'ORDER_SENT'
  | 'ORDER_CONFIRMED'
  | 'PENDING_PAYMENT'
  | 'PAID'
  | 'SHIPPED'
  | 'IN_WAREHOUSE';

export type ShippingType = 'DDP' | 'DAP' | 'EXW';

export interface PurchaseOrder {
  id: string;
  uuid: string;
  status: PurchaseOrderStatusType;
  postSalesManagerId?: string;
  postSalesManagerAssistantId?: string;
  supplierId?: string;
  supplier?: {
    id: string;
    name: string;
    labels?: string[];
    status: 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED';
    manualTags?: string[];
    additionalNotes?: string;
  };
  shippingInformation?: ShippingInfo;
  shippingType?: ShippingType;
  countryId?: string;
  state?: string;
  city?: string;
  streetAddress?: string;
  zip?: string;
  shippingCost?: number;
  attachmentId?: string;
  pdfGenerated?: boolean;
  formattedCreatedDate?: string;
  formattedLastModifiedDate?: string;
  createDate?: string;
  lastModifiedDate?: string;
}

export interface PurchaseOrderAssignments {
  postSalesManager?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  postSalesManagerAssistant?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface ShippingInfo {
  shippingType: ShippingType;
  countryId?: string;
  state?: string;
  city?: string;
  streetAddress?: string;
  zip?: string;
  shippingCost: number;
}

export const PurchaseOrderStatusTransitions: Record<PurchaseOrderStatusType, PurchaseOrderStatusType[]> = {
  PROCESSING: [],
  ORDER_SENT: ['ORDER_CONFIRMED', 'PENDING_PAYMENT', 'PROCESSING'],
  PENDING_PAYMENT: ['PAID'],
  PAID: ['ORDER_CONFIRMED', 'SHIPPED'],
  ORDER_CONFIRMED: ['SHIPPED'],
  SHIPPED: ['IN_WAREHOUSE'],
  IN_WAREHOUSE: [],
};

type ItemStatusType = {
  value: PurchaseOrderItemStatusType;
  label: string;
};
type PurchaseOrderStatusOptionsType = {
  value: PurchaseOrderStatusType;
  label: string;
};
export const StatusTypes: ItemStatusType[] = [
  {
    label: 'All',
    value: 'ALL',
  },
  {
    label: 'Ready to Order',
    value: 'READY_TO_ORDER',
  },
  {
    label: 'Price Requested',
    value: 'PRICE_REQUESTED',
  },
  {
    label: 'Awaiting Related Items',
    value: 'AWAITING_RELATED_ITEMS',
  },
];

export const PurchaseOrderStatusOptions: PurchaseOrderStatusOptionsType[] = [
  { label: 'Processing', value: 'PROCESSING' },
  { label: 'Order Sent', value: 'ORDER_SENT' },
  { label: 'Order Confirmed', value: 'ORDER_CONFIRMED' },
  { label: 'Pending Payment', value: 'PENDING_PAYMENT' },
  { label: 'Shipped', value: 'SHIPPED' },
  { label: 'In Warehouse', value: 'IN_WAREHOUSE' },
  { label: 'Paid', value: 'PAID' },
];

type StatusMapType = {
  label: string;
  color: string;
  dotColor: string;
};

export const PURCHASE_ORDER_ITEM_STATUS_MAPPING: Record<Exclude<PurchaseOrderItemStatusType, 'ALL'>, StatusMapType> = {
  READY_TO_ORDER: {
    label: 'Ready to Order',
    color: '#E3F4EE',
    dotColor: '#05796B',
  },
  PRICE_REQUESTED: {
    label: 'Price Requested',
    color: '#FFF1EB',
    dotColor: '#FF9865',
  },
  AWAITING_RELATED_ITEMS: {
    label: 'Awaiting Related Items',
    color: '#FFDCCB',
    dotColor: '#FE6D25',
  },
};
export const PURCHASE_ORDER_STATUS_MAPPING: Record<PurchaseOrderStatusType, StatusMapType> = {
  PROCESSING: {
    label: 'Processing',
    color: '#E6F0FF',
    dotColor: '#0D6EFD',
  },
  ORDER_SENT: {
    label: 'Order Sent',
    color: '#FEE0FF',
    dotColor: '#FF47E0',
  },
  ORDER_CONFIRMED: {
    label: 'Order Confirmed',
    color: '#DDE8E6',
    dotColor: '#05796B',
  },
  PENDING_PAYMENT: {
    label: 'Pending Payment',
    color: '#FFEEE0',
    dotColor: '#FF9865',
  },
  SHIPPED: {
    label: 'Shipped',
    color: '#EEEFFF',
    dotColor: '#5865F2',
  },
  IN_WAREHOUSE: {
    label: 'In Warehouse',
    color: '#E5F4FF',
    dotColor: '#15C1FF',
  },
  PAID: {
    label: 'Paid',
    color: '#E0FAE1',
    dotColor: '#44AE6F',
  },
};

export const SHIPPING_TYPE_OPTIONS = [
  { label: 'DDP (Delivered Duty Paid)', value: 'DDP' },
  { label: 'DAP (Delivered at Place)', value: 'DAP' },
  { label: 'EXW (Ex Works)', value: 'EXW' },
];

export const ALLOWED_STATUSES_FOR_SUPPLIER_OFFER_CREATION = ['PROCESSING', 'ORDER_SENT', 'PENDING_PAYMENT'];
